import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DriveBackupPanel } from "@/components/DriveBackupPanel";
import { ResetDataPanel } from "@/components/ResetDataPanel";
import { ResetLoansPanel } from "@/components/ResetLoansPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS = [
  ["entity_name", "Entity name (printed on T&C page and footer)"],
  ["abn", "ABN"],
  ["acn", "ACN"],
  ["phone", "Shop phone"],
  ["address", "Shop address"],
  ["dealer_name", "Default dealer name (pre-filled on buying contracts)"],
  ["kuber_qr_url", "Kuber scan-to-pay link (printed as a QR code on sales invoices)"],
  ["instagram_url", "Instagram link (shown on the follow-us page behind the social QR code)"],
  ["tiktok_url", "TikTok link"],
  ["facebook_url", "Facebook link (leave blank to hide it)"],
];

const DAYS = [
  ["mon", "Monday"],
  ["tue", "Tuesday"],
  ["wed", "Wednesday"],
  ["thu", "Thursday"],
  ["fri", "Friday"],
  ["sat", "Saturday"],
  ["sun", "Sunday"],
];

export default function BranchSettings() {
  const { branch } = useAuth();
  const [all, setAll] = useState({});
  const active = branch;
  const [saving, setSaving] = useState(false);
  const [footer, setFooter] = useState({ tagline: "", stores: [] });
  const [savingFooter, setSavingFooter] = useState(false);
  const [fees, setFees] = useState({});
  const [savingFees, setSavingFees] = useState(false);

  useEffect(() => {
    api
      .get("/settings/platform-fees")
      .then(({ data }) => setFees(data))
      .catch(() => setFees({}));
  }, []);

  const saveFees = async () => {
    setSavingFees(true);
    try {
      const { data } = await api.put("/settings/platform-fees", { rates: fees });
      setFees(data);
      toast.success("Platform handling fees saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingFees(false);
    }
  };

  useEffect(() => {
    api
      .get("/settings/branches")
      .then(({ data }) => setAll(data))
      .catch(() => setAll({}));
    api
      .get("/settings/footer")
      .then(({ data }) => setFooter(data))
      .catch(() => setFooter({ tagline: "", stores: [] }));
  }, []);

  const details = all[active] || {};
  const hours = details.trading_hours || {};

  const setHours = (day, patch) =>
    setAll({
      ...all,
      [active]: {
        ...details,
        trading_hours: { ...hours, [day]: { ...(hours[day] || {}), ...patch } },
      },
    });

  const saveFooter = async () => {
    setSavingFooter(true);
    try {
      await api.put("/settings/footer", footer);
      toast.success("Invoice footer saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingFooter(false);
    }
  };

  const updateStore = (i, key, value) =>
    setFooter({
      ...footer,
      stores: footer.stores.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)),
    });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/branches", { branch: active, details });
      setAll({ ...all, [active]: { ...details, ...data.details } });
      toast.success(`${active} details saved`);
      const { data: fresh } = await api.get("/settings/branches");
      setAll(fresh);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up space-y-6" data-testid="branch-settings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">Store Details</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            ABN, entity name, trading hours and contact details printed on{" "}
            <strong>{active}</strong> documents. To edit the other store, change the branch
            at the top of the screen.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="h-11 rounded-none bg-[#C5A059] px-6 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          data-testid="save-branch-settings-button"
        >
          <Save className="mr-2 h-4 w-4" /> Save details
        </Button>
      </div>

      <DriveBackupPanel />

      <div className="border border-border p-5" data-testid="platform-fees-panel">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-bold uppercase tracking-wider md:text-lg">
              Platform handling fees
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Percentage plus a fixed amount per transaction, applied to both stores. Update
              these whenever a provider changes their rates — set both to 0 to stop charging.
            </p>
          </div>
          <Button
            onClick={saveFees}
            disabled={savingFees}
            variant="outline"
            className="h-10 rounded-none"
            data-testid="save-platform-fees-button"
          >
            <Save className="mr-2 h-4 w-4" /> Save platform handling fees
          </Button>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Object.keys(fees)
            .sort()
            .map((method) => (
              <div key={method} className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {method}
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="number"
                      step="0.01"
                      className="h-10 rounded-none pr-6 text-right"
                      value={fees[method]?.pct ?? 0}
                      onChange={(e) =>
                        setFees({
                          ...fees,
                          [method]: { ...fees[method], pct: e.target.value },
                        })
                      }
                      data-testid={`fee-rate-${method.toLowerCase().replace(/ /g, "-")}`}
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      %
                    </span>
                  </div>
                  <div className="relative flex-1">
                    <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      $
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      className="h-10 rounded-none pl-5 text-right"
                      value={fees[method]?.fixed ?? 0}
                      onChange={(e) =>
                        setFees({
                          ...fees,
                          [method]: { ...fees[method], fixed: e.target.value },
                        })
                      }
                      data-testid={`fee-fixed-${method.toLowerCase().replace(/ /g, "-")}`}
                    />
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 border border-border p-5 sm:grid-cols-2">
        {FIELDS.map(([key, label]) => (
          <div key={key} className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {label}
            </Label>
            <Input
              className="h-10 rounded-none"
              value={details[key] || ""}
              onChange={(e) =>
                setAll({ ...all, [active]: { ...details, [key]: e.target.value } })
              }
              data-testid={`branch-field-${key}`}
            />
          </div>
        ))}
      </div>

      <div className="border border-border p-5" data-testid="trading-hours-panel">
        <h2 className="text-base font-bold uppercase tracking-wider md:text-lg">
          Trading hours — {active}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Used in the customer messages the portal writes for you, so nobody is told the
          shop is open on a day it is shut. Tick Closed for a day off.
        </p>
        <div className="mt-4 space-y-2">
          {DAYS.map(([day, label]) => {
            const entry = hours[day] || {};
            const closed = !!entry.closed;
            return (
              <div
                key={day}
                className="flex flex-wrap items-center gap-3 border-b border-border pb-2 last:border-0"
                data-testid={`trading-hours-row-${day}`}
              >
                <span className="w-24 text-sm font-semibold">{label}</span>
                <label className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={closed}
                    onChange={(e) =>
                      setHours(
                        day,
                        e.target.checked
                          ? { closed: true, open: "", close: "" }
                          : { closed: false, open: "10:00", close: "18:00" }
                      )
                    }
                    data-testid={`trading-hours-closed-${day}`}
                  />
                  Closed
                </label>
                {!closed && (
                  <>
                    <Input
                      type="time"
                      className="h-10 w-32 rounded-none"
                      value={entry.open || ""}
                      onChange={(e) => setHours(day, { open: e.target.value, closed: false })}
                      data-testid={`trading-hours-open-${day}`}
                    />
                    <span className="text-sm text-muted-foreground">to</span>
                    <Input
                      type="time"
                      className="h-10 w-32 rounded-none"
                      value={entry.close || ""}
                      onChange={(e) => setHours(day, { close: e.target.value, closed: false })}
                      data-testid={`trading-hours-close-${day}`}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
        {details.hours_sentence && (
          <p className="mt-4 text-xs text-muted-foreground" data-testid="trading-hours-preview">
            Customers will read: <strong>{details.hours_sentence}</strong>
          </p>
        )}
      </div>

      <div className="border border-border" data-testid="footer-editor">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-[#F8F9FA] px-4 py-3">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider">Invoice footer</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              The three store blocks printed at the bottom of every invoice.
            </p>
          </div>
          <Button
            onClick={saveFooter}
            disabled={savingFooter}
            className="h-10 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
            data-testid="save-footer-button"
          >
            <Save className="mr-2 h-4 w-4" /> Save footer
          </Button>
        </div>
        <div className="space-y-5 p-5">
          <div className="space-y-1.5 sm:max-w-sm">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Tagline
            </Label>
            <Input
              className="h-10 rounded-none"
              value={footer.tagline || ""}
              onChange={(e) => setFooter({ ...footer, tagline: e.target.value })}
              data-testid="footer-tagline-input"
            />
          </div>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {(footer.stores || []).map((s, i) => (
              <div key={i} className="space-y-3 border border-border p-4" data-testid={`footer-store-editor-${i}`}>
                {[
                  ["title", "Block heading"],
                  ["phone", "Phone / website"],
                  ["address", "Address / email"],
                  ["contact_label", "Contact name"],
                  ["contact_number", "Contact number"],
                ].map(([key, label]) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      {label}
                    </Label>
                    <Input
                      className="h-9 rounded-none"
                      value={s[key] || ""}
                      onChange={(e) => updateStore(i, key, e.target.value)}
                      data-testid={`footer-${i}-${key}`}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <ResetLoansPanel />
      <ResetDataPanel />
    </div>
  );
}
